import { Edit3, Mic, RotateCcw, X } from "lucide-react";
import { useMemo, useState } from "react";
import {
  MAX_QUEUED_AUDIO_BYTES_HARD,
  MAX_QUEUED_AUDIO_BYTES_WARNING,
  MAX_QUEUED_AUDIO_ITEMS_HARD,
  MAX_QUEUED_AUDIO_ITEMS_WARNING
} from "../services/app/constants";
import { conditionNotePath } from "../services/app/routes";
import { hasPendingSpokenNote } from "../services/app/reportStatus";
import { cancelRecording, retryRecording } from "../services/db/recordingRepo";
import type { Issue, Room, TranscriptionQueueJob } from "../services/db/schema";
import { notifyTranscriptionQueue } from "../services/sync/transcriptionQueue";
import { BlobImage } from "./BlobImage";
import { useDialog } from "./useDialog";

interface VoiceNotesStatusProps {
  rooms: Room[];
  issues: Issue[];
  jobs: TranscriptionQueueJob[];
  onNavigate: (path: string) => void;
  onChanged: () => void;
}

export interface VoiceQueueStorageState {
  activeItemCount: number;
  activeBytes: number;
  warning: boolean;
  hardLimit: boolean;
}

export function VoiceNotesStatus({
  rooms,
  issues,
  jobs,
  onNavigate,
  onChanged
}: VoiceNotesStatusProps): JSX.Element | null {
  const [open, setOpen] = useState(false);
  const issueById = useMemo(() => new Map(issues.map((issue) => [issue.id, issue])), [issues]);
  const roomById = useMemo(() => new Map(rooms.map((room) => [room.id, room])), [rooms]);
  const pendingSpokenIssues = issues.filter((issue) => hasPendingSpokenNote(issue));
  const visibleJobs = jobs.filter((job) => issueById.has(job.issueId));
  const summary = getVoiceSummaryLabel(visibleJobs, pendingSpokenIssues.length);
  const dialogRef = useDialog<HTMLElement>(open && Boolean(summary), () => setOpen(false));

  if (!summary) {
    return null;
  }

  async function retry(jobId: string): Promise<void> {
    await retryRecording(jobId);
    onChanged();
    notifyTranscriptionQueue();
  }

  async function cancel(jobId: string): Promise<void> {
    if (!window.confirm("Remove this recording? Other recordings and the photo will stay saved.")) return;
    await cancelRecording(jobId);
    onChanged();
    notifyTranscriptionQueue();
  }

  function openIssue(issue: Issue): void {
    setOpen(false);
    onNavigate(conditionNotePath(issue.roomId, issue.id));
  }

  return (
    <>
      <button
        className="voice-chip"
        type="button"
        aria-expanded={open}
        aria-controls="voice-notes-sheet"
        onClick={() => setOpen(true)}
      >
        <Mic size={16} aria-hidden="true" />
        <span>{summary}</span>
      </button>
      {open && (
        <div className="sheet-backdrop" role="presentation" onClick={() => setOpen(false)}>
          <section
            ref={dialogRef}
            className="voice-sheet"
            id="voice-notes-sheet"
            role="dialog"
            aria-modal="true"
            aria-labelledby="voice-sheet-title"
            data-swipe-ignore="true"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="voice-sheet__header">
              <div>
                <h2 id="voice-sheet-title">Voice notes</h2>
                <p>Voice notes are saved on this device. They transcribe when this app is open and online.</p>
              </div>
              <button className="icon-button" type="button" aria-label="Close voice notes" onClick={() => setOpen(false)}>
                <X size={18} aria-hidden="true" />
              </button>
            </div>
            <p className="helper">
              {visibleJobs.length + pendingSpokenIssues.length} {visibleJobs.length + pendingSpokenIssues.length === 1 ? "item" : "items"}
            </p>
            <div className="voice-sheet__list">
              {visibleJobs.map((job, index) => {
                const issue = issueById.get(job.issueId);
                if (!issue) {
                  return null;
                }
                const room = roomById.get(issue.roomId);
                return (
                  <article className="voice-row" key={job.jobId}>
                    <button className="voice-row__thumb" type="button" onClick={() => openIssue(issue)}>
                      <BlobImage blobKey={issue.photoThumbBlobKey} alt="" />
                    </button>
                    <div className="voice-row__body">
                      <div className="voice-row__meta">
                        <strong>{room?.name ?? "Room"}</strong>
                        <span>{formatJobStatus(job, index, visibleJobs.length)}</span>
                      </div>
                      <p>{formatJobDetail(job)}</p>
                      <div className="voice-row__actions">
                        <button className="button" type="button" onClick={() => openIssue(issue)}>
                          <Edit3 size={16} aria-hidden="true" />
                          Open note
                        </button>
                        {canRetryJob(job) && (
                          <button className="button" type="button" onClick={() => void retry(job.jobId)}>
                            <RotateCcw size={16} aria-hidden="true" />
                            Retry now
                          </button>
                        )}
                        <button className="button button--danger" type="button" onClick={() => void cancel(job.jobId)}>
                          Remove this recording
                        </button>
                      </div>
                    </div>
                  </article>
                );
              })}
              {pendingSpokenIssues.map((issue) => {
                const room = roomById.get(issue.roomId);
                return (
                  <article className="voice-row" key={`candidate-${issue.id}`}>
                    <button className="voice-row__thumb" type="button" onClick={() => openIssue(issue)}>
                      <BlobImage blobKey={issue.photoThumbBlobKey} alt="" />
                    </button>
                    <div className="voice-row__body">
                      <div className="voice-row__meta">
                        <strong>{room?.name ?? "Room"}</strong>
                        <span>Spoken note ready</span>
                      </div>
                      <p>Open the condition note to use it or keep your typed note.</p>
                      <div className="voice-row__actions">
                        <button className="button button--primary" type="button" onClick={() => openIssue(issue)}>
                          <Edit3 size={16} aria-hidden="true" />
                          Open note
                        </button>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          </section>
        </div>
      )}
    </>
  );
}

export function getVoiceQueueStorageState(
  issues: readonly Issue[],
  jobs: readonly TranscriptionQueueJob[]
): VoiceQueueStorageState {
  const activeBytes = jobs.reduce((sum, job) => sum + (job.audioSizeBytes ??
    issues.find((issue) => issue.audioBlobKey === job.audioBlobKey)?.audioSizeBytes ?? 0), 0);
  const activeItemCount = jobs.length;
  return {
    activeItemCount,
    activeBytes,
    warning: activeItemCount >= MAX_QUEUED_AUDIO_ITEMS_WARNING || activeBytes >= MAX_QUEUED_AUDIO_BYTES_WARNING,
    hardLimit: activeItemCount >= MAX_QUEUED_AUDIO_ITEMS_HARD || activeBytes >= MAX_QUEUED_AUDIO_BYTES_HARD
  };
}

function getVoiceSummaryLabel(jobs: readonly TranscriptionQueueJob[], spokenReadyCount: number): string | undefined {
  const errors = jobs.filter((job) => job.status === "error").length;
  if (errors > 0) {
    return "Voice note needs attention";
  }
  const uploading = jobs.filter((job) => job.status === "uploading").length;
  if (uploading > 0) {
    return `Uploading ${uploading} of ${jobs.length}`;
  }
  if (spokenReadyCount > 0) {
    return `${spokenReadyCount} spoken ${spokenReadyCount === 1 ? "note" : "notes"} ready`;
  }
  if (jobs.length === 1) {
    return "1 voice note waiting";
  }
  if (jobs.length > 1) {
    return `${jobs.length} voice notes queued`;
  }
  return undefined;
}

function formatJobStatus(job: TranscriptionQueueJob, index: number, total: number): string {
  if (job.status === "uploading") {
    return `Uploading ${index + 1} of ${total}`;
  }
  if (job.status === "transcribing") {
    return "Transcribing";
  }
  if (job.status === "retry_wait") {
    return "Retry waiting";
  }
  if (job.status === "error") {
    return "Needs attention";
  }
  return "Waiting";
}

function canRetryJob(job: TranscriptionQueueJob): boolean {
  return job.status === "queued" || job.status === "retry_wait" || job.status === "error";
}

function formatJobDetail(job: TranscriptionQueueJob): string {
  if (job.status === "uploading") {
    if (job.uploadTotalBytes && job.uploadLoadedBytes !== undefined) {
      const percent = Math.min(100, Math.round((job.uploadLoadedBytes / job.uploadTotalBytes) * 100));
      return `Uploading voice note... ${percent}%`;
    }
    return "Uploading voice note...";
  }
  if (job.status === "transcribing") {
    return "Uploaded. Transcribing voice note...";
  }
  if (job.status === "retry_wait") {
    return `Could not transcribe yet. Retrying ${formatRetry(job.nextAttemptAt)}.`;
  }
  if (job.status === "error") {
    return job.lastError || "Could not transcribe. Type a note or retry.";
  }
  return `Saved ${formatAge(job.createdAt)}.`;
}

function formatRetry(nextAttemptAt: string): string {
  const remainingMs = Date.parse(nextAttemptAt) - Date.now();
  if (!Number.isFinite(remainingMs) || remainingMs <= 0) {
    return "soon";
  }
  const seconds = Math.ceil(remainingMs / 1000);
  return seconds < 60 ? `in ${seconds}s` : `in ${Math.ceil(seconds / 60)} min`;
}

function formatAge(createdAt: string): string {
  const ageMs = Date.now() - Date.parse(createdAt);
  if (!Number.isFinite(ageMs) || ageMs < 60_000) {
    return "just now";
  }
  const minutes = Math.floor(ageMs / 60_000);
  if (minutes < 60) {
    return `${minutes}m ago`;
  }
  return `${Math.floor(minutes / 60)}h ago`;
}
